/**
 * Key-builders for the overview cards' copy.
 *
 * The four cards share a key shape, so the shape is written once here rather
 * than as four sets of hand-typed dotted strings — a typo in one of those is a
 * key rendered raw on screen, which no type checker catches.
 */
const ROOT = 'analytics.financialPosition.overview';

/** `overviewKey('cashRunway', 'title')` → the full `pages` namespace key. */
export const overviewKey = (card: string, leaf: string): string =>
    `${ROOT}.${card}.${leaf}`;

/** Copy for the boundary every card shares. */
export const OVERVIEW_ERROR_KEYS = {
    title: `${ROOT}.errors.title`,
    message: `${ROOT}.errors.message`,
    retry: `${ROOT}.errors.retry`,
};

/**
 * Sentences that belong to a *reason*, not to a card.
 *
 * "The ledger has nothing posted yet" explains more than "no due dates" and is
 * true of every card at once, so when the server reports it, it wins over the
 * card's own wording. Anything not listed here leaves the card to say what it
 * is personally waiting for.
 */
const SHARED_MISSING_KEYS: Record<string, string> = {
    NO_LEDGER_BALANCES: `${ROOT}.missing.noLedgerBalances`,
    NO_CASH_ACCOUNT: `${ROOT}.missing.noCashAccount`,
};

/**
 * Which sentence an empty card shows: the shared explanation when the server
 * reported one, otherwise the card's own.
 */
export const resolveEmptyBodyKey = (
    fallbackKey: string,
    missing: readonly string[],
): string => {
    const shared = missing
        .map((code) => SHARED_MISSING_KEYS[code])
        .find((key): key is string => key !== undefined);

    return shared ?? fallbackKey;
};
